import React from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import ArrowForward from '@mui/icons-material/ArrowForward';

import { makeStyles } from 'tss-react/mui';
import { DateTime } from 'luxon';

const useStyles = makeStyles()((theme) => ({
  button: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    maxWidth: 5150,
    minWidth: 200,
    boxShadow: theme.shadows[3],
    transition: '300ms',
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.border.light}`,
    borderRadius: 12,
    padding: 16,
    '&:hover': {
      cursor: 'pointer',
      boxShadow: theme.shadows[5],
      background: theme.palette.action.hover,
      backgroundColor: theme.palette.action.hover,
    },
  },
  secondaryText: {
    color: theme.palette.text.secondary,
  },
  disabled: {
    opacity: 0.5,
  },
  mainText: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  textSummary: {
    flex: 1,
    width: '100%',
    marginTop: 8,
    padding: 16,
    verticalAlign: 'middle',
    borderRadius: 12,
    backgroundColor: theme.palette.info.light,
    color: theme.palette.info.dark,
  },
}));

export type QFrequencyProps = {
  id?: string;
  className?: string;
  onClick?: () => void;
  /** Opens the frequency editor, separately from the card's own `onClick`. Falls back to `onClick` when omitted, so the whole card (including the "Edit" button) behaves as one click target. */
  onEditClick?: () => void;
  dueOn?: DateTime | null;
  endOn?: DateTime | null;
  frequency?: string;
  summaryText?: string;
};

const QFrequency = React.forwardRef((props: QFrequencyProps, ref: React.ForwardedRef<HTMLDivElement>) => {
  const { id = 'qFrequency', dueOn, endOn, frequency, summaryText, className, onClick, onEditClick } = props;
  const { classes, cx } = useStyles();

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (onClick && e.keyCode === 13) onClick();
  };

  return (
    <ButtonBase
      id={id}
      data-testid={id}
      component={Paper}
      elevation={4}
      onKeyDown={handleKeyDown}
      onClick={onClick}
      classes={{ disabled: classes.disabled }}
      ref={ref}
      className={cx(classes.button, className)}
      sharedcomponentid='Q_FREQUENCY'
    >
      <Box display='flex' alignItems='center' justifyContent='space-between' width='100%' height='100%'>
        <Box display='flex' gap='40px'>
          <Box display='flex' flexDirection='column' justifyContent='space-between'>
            <Typography variant='captionRegular' className={classes.secondaryText}>
              Next Due Date
            </Typography>
            <Typography className={classes.mainText} variant='bodyRegular'>
              {dueOn?.isValid ? dueOn.toLocaleString(DateTime.DATE_MED) : '-'}
            </Typography>
          </Box>
          <Box display='flex' flexDirection='column' justifyContent='space-between'>
            <Typography variant='captionRegular' className={classes.secondaryText}>
              Frequency
            </Typography>
            <Typography className={classes.mainText} variant='bodyRegular'>
              {frequency?.replace(/[0-9]/g, 'X')}
            </Typography>
          </Box>
          <Box display='flex' flexDirection='column' justifyContent='space-between'>
            <Typography variant='captionRegular' className={classes.secondaryText}>
              End Date
            </Typography>
            <Typography className={classes.mainText} variant='bodyRegular'>
              {endOn?.isValid ? endOn.toLocaleString(DateTime.DATE_MED) : '-'}
            </Typography>
          </Box>
        </Box>

        <Button
          variant='text'
          id='change-freq'
          endIcon={<ArrowForward />}
          onClick={(e) => {
            if (onEditClick) {
              e.stopPropagation();
              onEditClick();
            } else {
              onClick?.();
            }
          }}
        >
          Edit
        </Button>
      </Box>

      {summaryText ? (
        <Typography variant='captionRegular' className={classes.textSummary}>
          {summaryText}
        </Typography>
      ) : null}
    </ButtonBase>
  );
});

QFrequency.displayName = 'QFrequency';

export default QFrequency;
